// Scoring engine: features → Overall Gait Score (0–100) + five subscores.
//
// Higher = better. Scores are built from normalized, scale-invariant features so
// they are comparable across follow-ups and between sides. View-aware: a
// subscore that a given view can't support is omitted, and the Overall is
// renormalized over the subscores actually computed.

import type { FrontalFeatures } from "./frontal";
import type { LateralFeatures } from "./lateral";
import type { TemporalSummary } from "./temporal";
import { clamp } from "./geometry";
import { deriveConfidence, type Confidence } from "./bands";

export const SCORING_VERSION = "phase2-3-normalzone";

// Frontal x-based severe references rescaled to isotropic space (× 9/16), in
// step with the phase2-seed-2 banding. y-based (pelvicDrop) and ratio
// (asymmetryIndex, trendelenburg) references are aspect-invariant. See bands.ts.
const FRONTAL_ISO = 9 / 16;

export interface Scores {
  overall: number;
  symmetry?: number;
  pelvicStability?: number;
  kneeControl?: number;
  trunkStability?: number;
  consistency?: number;
  confidence: Confidence;
}

export interface ScoreInput {
  view: "frontal" | "lateral";
  temporal: TemporalSummary;
  frontal?: FrontalFeatures;
  lateral?: LateralFeatures;
  meanVisibility: number;
  cycleCount: number;
}

/**
 * penalty = magnitude scaled against a "severe" reference, 0..100 — but only
 * ABOVE `normalCut`. Below `normalCut` the penalty is 0.
 *
 * Before phase2-3-normalzone, this scaled linearly from magnitude=0, with no
 * tolerance zone at all — so a measurement bands.ts itself classifies as
 * NORMAL (below its own normalCut) still dragged the score down. Across five
 * independent subscores that compounds into an overall score that looks
 * "pathological" for an unremarkable, healthy gait (reported: 46/100 for a
 * self-tested healthy frontal walk). `normalCut` is always the matching
 * NORMAL/LOW band boundary already defined and versioned in bands.ts — this
 * reuses existing seed values instead of introducing new unvalidated
 * numbers; see docs/THRESHOLD_CALIBRATION_GATE.md for the rationale record.
 */
function penalty(magnitude: number, normalCut: number, severeRef: number): number {
  if (severeRef <= normalCut) return 0;
  if (magnitude <= normalCut) return 0;
  return clamp(((magnitude - normalCut) / (severeRef - normalCut)) * 100, 0, 100);
}
const scoreFrom = (...penalties: number[]) =>
  Math.round(clamp(100 - avg(penalties), 0, 100));
const avg = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0);

const WEIGHTS = {
  symmetry: 0.25,
  pelvicStability: 0.2,
  kneeControl: 0.2,
  trunkStability: 0.15,
  consistency: 0.2,
};

export function computeScores(input: ScoreInput): Scores {
  const { temporal, frontal, lateral } = input;
  const sub: Partial<Record<keyof typeof WEIGHTS, number>> = {};

  // --- Symmetry ---
  // normalCut values mirror bands.ts's stance_symmetry [5,10,18] and
  // frontal_asymmetry [10,20,30] NORMAL boundaries (first cut).
  const symP: number[] = [];
  if (temporal.stanceSymmetryIndex !== null)
    symP.push(penalty(temporal.stanceSymmetryIndex, 5, 25));
  if (frontal) symP.push(penalty(frontal.asymmetryIndex, 10, 30));
  if (symP.length) sub.symmetry = scoreFrom(...symP);

  // --- Pelvic stability (frontal) ---
  // normalCut mirrors bands.ts pelvic_drop [0.03,0.05,0.08] and the
  // trendelenburg tendency LOW boundary (0.34).
  if (frontal) {
    sub.pelvicStability = scoreFrom(
      penalty(frontal.pelvicDrop, 0.03, 0.09),
      penalty(frontal.trendelenburg, 0.34, 1),
    );
  }

  // --- Knee control ---
  // normalCut mirrors bands.ts knee_collapse tendency LOW boundary
  // (0.04 x FRONTAL_ISO) and the lateral knee_flexion/terminal_extension
  // NORMAL boundaries ([8,18,28] and [8,15,25]).
  const kneeP: number[] = [];
  if (frontal)
    kneeP.push(
      penalty(
        Math.max(frontal.kneeCollapse.L, frontal.kneeCollapse.R),
        0.04 * FRONTAL_ISO,
        0.09 * FRONTAL_ISO,
      ),
    );
  if (lateral) {
    const flexDeficit = Math.max(
      Math.max(0, 50 - lateral.kneeFlexionExcursion.L),
      Math.max(0, 50 - lateral.kneeFlexionExcursion.R),
    );
    const extDeficit = Math.max(
      lateral.terminalExtensionDeficit.L,
      lateral.terminalExtensionDeficit.R,
    );
    kneeP.push(penalty(flexDeficit, 8, 30), penalty(extDeficit, 8, 25));
  }
  if (kneeP.length) sub.kneeControl = scoreFrom(...kneeP);

  // --- Trunk stability ---
  // normalCut mirrors bands.ts trunk_lean [0.04,0.07,0.1] x FRONTAL_ISO and
  // trunk_posture (lateral trunkForwardLean) [8,14,20].
  const trunkP: number[] = [];
  if (frontal) trunkP.push(penalty(frontal.trunkLean, 0.04 * FRONTAL_ISO, 0.11 * FRONTAL_ISO));
  if (lateral) trunkP.push(penalty(lateral.trunkForwardLean, 8, 22));
  if (trunkP.length) sub.trunkStability = scoreFrom(...trunkP);

  // --- Consistency ---
  // normalCut mirrors bands.ts cycle_consistency/stride_regularity [8,15,25]
  // (cycle_consistency actually uses [5,10,18] — kept distinct below) and
  // smoothness [0.15,0.3,0.5].
  const consP: number[] = [penalty(temporal.cycleConsistencyCV * 100, 5, 20)];
  if (lateral) {
    consP.push(penalty(lateral.strideRegularityCV * 100, 8, 25));
    consP.push(penalty(lateral.smoothnessJerk, 0.15, 0.5));
  }
  sub.consistency = scoreFrom(...consP);

  // --- Overall: renormalize weights over available subscores ---
  let wSum = 0;
  let acc = 0;
  (Object.keys(WEIGHTS) as Array<keyof typeof WEIGHTS>).forEach((k) => {
    const v = sub[k];
    if (v !== undefined) {
      acc += v * WEIGHTS[k];
      wSum += WEIGHTS[k];
    }
  });
  const overall = wSum > 0 ? Math.round(acc / wSum) : 0;

  return {
    overall,
    ...sub,
    confidence: deriveConfidence(input.meanVisibility, input.cycleCount),
  };
}
